import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, num, day, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { SupplierModal, BillModal, BillView, PaymentModal, BILL_STATUS } from '../components/SupplierModals';

// One supplier: what they supply, what they have billed, what has been paid and
// what is still owed. Everything on this page is scoped to this supplier.
const SupplierDetail = () => {
  const { id } = useParams();
  const { can } = useAuth();
  const canWrite = can('suppliers:write');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [billing, setBilling] = useState(false);
  const [editBill, setEditBill] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/suppliers/${id}`).then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  if (loading) return <div className="spinner" />;
  if (!data) return null;
  const { supplier: s, materials, supplies, bills, payments, balance } = data;
  const openBills = bills.filter((b) => b.balance > 0.005 && b.status !== 'cancelled');

  return (
    <div>
      <div className="section-head">
        <div>
          <h2 style={{ margin: 0 }}>{s.name}</h2>
          <span className="muted small">
            {[s.contactName, s.phone, s.email].filter(Boolean).join(' · ') || 'No contact details'}
            {s.gstin ? ` · GSTIN ${s.gstin}` : ''}{s.terms ? ` · ${s.terms}` : ''}
            {!s.isActive && ' · inactive'}
          </span>
        </div>
        <div className="flex gap">
          <Link to="/suppliers" className="btn btn-outline btn-sm">‹ All suppliers</Link>
          {canWrite && <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>}
          {canWrite && <button className="btn btn-outline btn-sm" onClick={() => setBilling(true)}>+ Bill</button>}
          {canWrite && <button className="btn btn-primary btn-sm" onClick={() => setPaying(true)}>Pay</button>}
        </div>
      </div>

      <div className="tiles">
        <div className="tile"><div className="k">Outstanding</div><div className="v">{inr(Math.max(0, balance.outstanding))}</div></div>
        <div className="tile"><div className="k">Billed</div><div className="v">{inr(balance.billed)}</div></div>
        <div className="tile"><div className="k">Paid</div><div className="v">{inr(balance.paidTotal)}</div></div>
        <div className="tile"><div className="k">Advance held</div><div className="v">{inr(balance.advance)}</div></div>
        <div className="tile"><div className="k">Open bills</div><div className="v">{openBills.length}</div></div>
      </div>

      {/* What they supply: raw materials assigned to them + products ordered from them */}
      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Raw materials ({materials.length})</h3>
        {materials.length ? (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Material</th><th>Kind</th><th>Unit</th><th className="right">Purchase price</th><th className="right">Min stock</th><th>Status</th></tr></thead>
              <tbody>
                {materials.map((m) => (
                  <tr key={m.id}>
                    <td><Link to={`/raw-materials/${m.id}`} className="link-strong">{m.name}</Link></td>
                    <td className="muted small">{m.kind || '—'}</td>
                    <td className="muted small">{m.unit}</td>
                    <td className="right">{inr(m.purchasePrice)}</td>
                    <td className="right">{num(m.minStock)}</td>
                    <td><span className={`badge ${m.isActive ? 'badge-green' : 'badge-gray'}`}>{m.isActive ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="muted small">No raw materials point at this supplier yet — set the supplier on a material to list it here.</p>}
      </div>

      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Products ordered ({supplies.length})</h3>
        {supplies.length ? (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Product</th><th>Category</th><th className="right">Qty ordered</th><th className="right">Last rate</th><th>Last ordered</th><th className="right">Orders</th></tr></thead>
              <tbody>
                {supplies.map((p) => (
                  <tr key={p.productId}>
                    <td><Link to={`/products/${p.productId}`} className="link-strong">{p.name}</Link></td>
                    <td className="muted small">{p.category || '—'}</td>
                    <td className="right">{num(p.qty)} {p.unit || ''}</td>
                    <td className="right">{inr(p.lastRate)}</td>
                    <td className="muted small">{day(p.lastOrdered)}</td>
                    <td className="right">{p.orders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="muted small">Nothing ordered from them yet — vendor purchase orders are raised in <Link to="/procurement">Procurement</Link>.</p>}
      </div>

      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Bills ({bills.length})</h3>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Bill</th><th>Date</th><th>Due</th><th className="right">Total</th><th className="right">Paid</th><th className="right">Balance</th><th>Status</th></tr></thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => setViewId(b.id)} title="Open bill">
                  <td><strong>{b.code}</strong>{b.billNumber ? <span className="muted small"> · {b.billNumber}</span> : null}</td>
                  <td className="muted small">{day(b.billDate)}</td>
                  <td className="muted small">{b.dueDate ? day(b.dueDate) : '—'}</td>
                  <td className="right">{inr(b.total)}</td>
                  <td className="right">{Number(b.paidAmount) ? inr(b.paidAmount) : '—'}</td>
                  <td className="right"><strong>{b.balance > 0.005 ? inr(b.balance) : '—'}</strong></td>
                  <td><span className={`badge ${BILL_STATUS[b.status] || 'badge-gray'}`}>{b.status}</span></td>
                </tr>
              ))}
              {!bills.length && <tr><td colSpan={7} className="empty">No bills recorded.</td></tr>}
            </tbody>
            {bills.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={3}><strong>Total</strong></td>
                <td className="right">{inr(bills.reduce((t, b) => t + Number(b.total), 0))}</td>
                <td className="right">{inr(bills.reduce((t, b) => t + Number(b.paidAmount), 0))}</td>
                <td className="right"><strong>{inr(bills.reduce((t, b) => t + Number(b.balance), 0))}</strong></td>
                <td></td>
              </tr></tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Payments ({payments.length})</h3>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Payment</th><th>Date</th><th>Mode</th><th>Reference</th><th className="right">Amount</th><th className="right">Unapplied</th></tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.code}</strong></td>
                  <td className="muted small">{day(p.paymentDate)}</td>
                  <td>{(p.mode || '').toUpperCase()}</td>
                  <td className="muted small">{p.reference || '—'}</td>
                  <td className="right">{inr(p.amount)}</td>
                  <td className="right">{p.unallocated > 0.005 ? inr(p.unallocated) : '—'}</td>
                </tr>
              ))}
              {!payments.length && <tr><td colSpan={6} className="empty">No payments recorded.</td></tr>}
            </tbody>
            {payments.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={4}><strong>Total</strong></td>
                <td className="right"><strong>{inr(payments.reduce((t, p) => t + Number(p.amount), 0))}</strong></td>
                <td className="right">{inr(payments.reduce((t, p) => t + Number(p.unallocated), 0))}</td>
              </tr></tfoot>
            )}
          </table>
        </div>
      </div>

      {editing && <SupplierModal supplier={s} onClose={() => setEditing(false)} onSaved={load} />}
      {billing && <BillModal suppliers={[s]} presetSupplierId={s.id} onClose={() => setBilling(false)} onSaved={load} />}
      {editBill && <BillModal bill={editBill} suppliers={[s]} onClose={() => setEditBill(null)} onSaved={load} />}
      {viewId && <BillView billId={viewId} canWrite={canWrite} onClose={() => setViewId(null)} onChanged={load} onEdit={setEditBill} />}
      {paying && <PaymentModal suppliers={[{ ...s, outstanding: balance.outstanding }]} presetSupplierId={s.id} onClose={() => setPaying(false)} onSaved={load} />}
    </div>
  );
};

export default SupplierDetail;
